import { motion } from 'framer-motion';

import { RoomHotseat } from '@/api/generated/ws-protocol';
import {
  selectActivePlayer,
  selectCanAct,
  selectNextPlayer,
  useActions,
  useGame,
} from '@/store/game';
import { RejectionNotice } from '@/ui/RejectionNotice';

import { PauseButton } from './PauseButton';
import { DECK_ORDER, deckLook } from './decks';

/**
 * 03 · Ход. Кто ходит, что осталось в колодах, кнопка «тянуть».
 *
 * Отступление от макета: там мета «КРУГ N · КАРТА M», здесь «ХОД N». Круг из
 * `turn` выводится только через допущение «за круг каждый тянет ровно раз» —
 * это правило игры, а правил на клиенте нет (CLAUDE.md, инвариант 1). Придёт
 * поле — вернём формулировку макета.
 *
 * Кнопка своя, а не Telegram MainButton: по макету это поле в 180 px на пол-экрана,
 * промахнуться по которому невозможно даже вслепую. Нативная кромка такого не даёт.
 */
export function TurnScreen() {
  const state = useGame((s) => s.state);
  const roomMode = useGame((s) => s.roomMode);
  const active = useGame(selectActivePlayer);
  const next = useGame(selectNextPlayer);
  const canAct = useGame(selectCanAct);
  const { draw } = useActions();

  if (!state) return null;

  const remaining = DECK_ORDER.map((deck) => ({ deck, left: state.remaining[deck] ?? 0 }));
  const most = Math.max(1, ...remaining.map((r) => r.left));

  return (
    <div
      className="flex min-h-screen flex-col bg-ground px-5 text-ink"
      style={{ paddingTop: 'var(--clown-safe-top)' }}
    >
      <div className="flex-1 py-6">
        <div className="flex items-baseline justify-between">
          <p className="text-meta uppercase tracking-widest text-mute">Ход {state.turn}</p>
          <PauseButton />
        </div>

        <p className="mt-6 text-meta uppercase tracking-widest text-mute">Ходит</p>
        <p className="font-display text-name text-acc">{active?.name ?? '—'}</p>

        <div className="mt-6 border-t-2 border-line pt-3">
          <p className="text-lead text-dim">
            {roomMode === RoomHotseat
              ? `Следующий — ${next?.name ?? '—'}.`
              : canAct
                ? 'Твой ход. Остальные ждут.'
                : `Ждём, пока ${active?.name ?? 'игрок'} потянет карточку.`}
          </p>
        </div>

        <div className="mt-8">
          <div className="flex h-tap items-end gap-3">
            {remaining.map(({ deck, left }) => (
              <motion.div
                key={deck}
                data-testid={`remaining-${deck}`}
                className={`w-bar ${deckLook(deck).bar} ${left === 0 ? 'opacity-35' : ''}`}
                // Высота столбика — доля от самой полной колоды. Это отрисовка
                // числа из состояния, а не расчёт правил. Растёт она заметно:
                // так видно, что колода убывает, без единой подписи.
                initial={false}
                animate={{ height: `${Math.max(4, Math.round((left / most) * 100))}%` }}
                transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
              />
            ))}
          </div>
          <p className="mt-3 text-meta uppercase tracking-widest text-mute">
            Осталось в колодах: {remaining.map((r) => r.left).join(' · ')}
          </p>
        </div>

        <RejectionNotice />
      </div>

      {/* У неактивного игрока в multi кнопки нет вовсе — спокойный экран ожидания. */}
      {canAct && (
        <button
          type="button"
          onClick={draw}
          className="-mx-5 flex h-draw flex-col justify-between bg-acc p-5 text-left text-ground"
          style={{ marginBottom: 'var(--clown-safe-bottom)' }}
        >
          <span className="font-display text-turn">ТЯНУТЬ КАРТУ</span>
          <span className="flex w-full items-center justify-between text-meta uppercase tracking-widest">
            Тап в любое место <span aria-hidden>→</span>
          </span>
        </button>
      )}
    </div>
  );
}
